import { motion } from "motion/react";

export type CourseLevel = "입문" | "실전" | "마스터" | "all";

interface LearningRoadmapProps {
  activeLevel: CourseLevel;
  onLevelChange: (level: CourseLevel) => void;
  courseCounts: Record<CourseLevel, number>;
}

const steps: { level: CourseLevel; label: string; subtitle: string; icon: string }[] = [
  {
    level: "입문",
    label: "Step 1. 입문",
    subtitle: "기초 & 용어",
    icon: "ri-seedling-line",
  },
  {
    level: "실전",
    label: "Step 2. 실전",
    subtitle: "권리분석 & 시세",
    icon: "ri-compass-3-line",
  },
  {
    level: "마스터",
    label: "Step 3. 마스터",
    subtitle: "낙찰 & 명도",
    icon: "ri-award-line",
  },
];

export default function LearningRoadmap({
  activeLevel,
  onLevelChange,
  courseCounts,
}: LearningRoadmapProps) {
  const activeIndex = activeLevel === "all" ? -1 : steps.findIndex((s) => s.level === activeLevel);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
      className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8 mb-10"
    >
      <div className="flex items-center gap-2 mb-6">
        <i className="ri-signpost-line text-[#C4956A]"></i>
        <h2 className="text-base font-bold text-[#1C1917]">경매 학습 로드맵</h2>
        <span className="text-xs text-[#8C8780] ml-1">당신의 현재 단계를 선택하세요</span>
      </div>

      <div className="relative">
        {/* Connecting line */}
        <div className="absolute top-10 left-0 right-0 hidden md:block">
          <div className="h-[2px] bg-[#E8E5E0] mx-16">
            {activeIndex >= 0 && (
              <motion.div
                className="h-full bg-[#C4956A]"
                initial={{ width: "0%" }}
                animate={{
                  width: activeIndex === 0 ? "0%" : activeIndex === 1 ? "50%" : "100%",
                }}
                transition={{ duration: 0.6, ease: "easeOut" as const }}
              />
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="flex flex-col md:flex-row items-stretch md:items-start gap-4 md:gap-0 justify-between relative">
          {/* 전체 보기 */}
          <div className="flex items-center md:flex-col md:items-center gap-3 md:gap-0 md:w-1/4">
            <button
              onClick={() => onLevelChange("all")}
              className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-xl transition-all relative z-10 ${
                activeLevel === "all"
                  ? "bg-[#1C1917] text-white shadow-lg shadow-[#1C1917]/20 scale-110"
                  : "bg-[#FAFAF8] border-2 border-[#E8E5E0] text-[#8C8780] hover:border-[#C4956A]/50 hover:text-[#C4956A]"
              }`}
            >
              <i className="ri-global-line"></i>
            </button>
            <div className="md:mt-3 text-left md:text-center">
              <p className={`text-xs font-bold ${activeLevel === "all" ? "text-[#1C1917]" : "text-[#5C5650]"}`}>
                전체
              </p>
              <p className="text-[10px] text-[#8C8780] mt-0.5">
                {courseCounts.all}개 과정
              </p>
            </div>
          </div>

          {/* 3 steps */}
          {steps.map((step, idx) => {
            const isActive = activeLevel === step.level;
            const isPassed = activeIndex > idx && activeLevel !== "all";

            return (
              <div
                key={step.level}
                className="flex items-center md:flex-col md:items-center gap-3 md:gap-0 md:w-1/4"
              >
                <button
                  onClick={() => onLevelChange(isActive ? "all" : step.level)}
                  className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-xl transition-all relative z-10 ${
                    isActive
                      ? "bg-[#C4956A] text-white shadow-lg shadow-[#C4956A]/25 scale-110"
                      : isPassed
                      ? "bg-[#C4956A]/20 text-[#C4956A] border-2 border-[#C4956A]/30"
                      : "bg-[#FAFAF8] border-2 border-[#E8E5E0] text-[#8C8780] hover:border-[#C4956A]/50 hover:text-[#C4956A]"
                  }`}
                >
                  {isPassed ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    <i className={step.icon}></i>
                  )}
                </button>
                <div className="md:mt-3 text-left md:text-center">
                  <p
                    className={`text-xs font-bold ${
                      isActive ? "text-[#C4956A]" : isPassed ? "text-[#C4956A]/70" : "text-[#5C5650]"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-[10px] text-[#8C8780] mt-0.5">
                    {step.subtitle}
                  </p>
                  <p className="text-[10px] text-[#B8B3AE] mt-0.5">
                    {courseCounts[step.level]}개 과정
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}